/**
 * Types of events that award XP
 */
export type XPEventType =
  | 'LESSON_COMPLETE'
  | 'VIDEO_WATCH'
  | 'QUIZ_PASS'
  | 'QUIZ_PERFECT'
  | 'DAILY_STREAK'
  | 'FIRST_LESSON_OF_DAY'
  | 'WEEK_MILESTONE'
  | 'MANUAL_AWARD';

export interface XPEventConfig {
  baseXP: number;
  label: string;
  description: string;
  icon: string;
}

export const XP_EVENT_CONFIG: Record<XPEventType, XPEventConfig> = {
  LESSON_COMPLETE: {
    baseXP: 10,
    label: 'Lección completada',
    description: 'Completar una lección del curso',
    icon: '📚',
  },
  VIDEO_WATCH: {
    baseXP: 5,
    label: 'Video visto',
    description: 'Ver un video completo (>90%)',
    icon: '🎬',
  },
  QUIZ_PASS: {
    baseXP: 20,
    label: 'Quiz aprobado',
    description: 'Aprobar un quiz (≥60%)',
    icon: '✅',
  },
  QUIZ_PERFECT: {
    baseXP: 50,
    label: 'Quiz perfecto',
    description: 'Obtener 100% en un quiz',
    icon: '🌟',
  },
  DAILY_STREAK: {
    baseXP: 1, // Multiplied by streak days (max 30)
    label: 'Racha diaria',
    description: 'Bonus por días consecutivos de actividad',
    icon: '🔥',
  },
  FIRST_LESSON_OF_DAY: {
    baseXP: 5,
    label: 'Primera lección del día',
    description: 'Completar la primera lección del día',
    icon: '🌅',
  },
  WEEK_MILESTONE: {
    baseXP: 100,
    label: 'Hito semanal',
    description: 'Completar 10 lecciones en una semana',
    icon: '🏆',
  },
  MANUAL_AWARD: {
    baseXP: 0, // Variable
    label: 'Premio manual',
    description: 'XP otorgado por el profesor',
    icon: '🎁',
  },
};

export function getXPEventConfig(eventType: XPEventType): XPEventConfig {
  return XP_EVENT_CONFIG[eventType];
}

export function getBaseXP(eventType: XPEventType): number {
  return XP_EVENT_CONFIG[eventType].baseXP;
}

export function getXPEventLabel(eventType: XPEventType): string {
  return XP_EVENT_CONFIG[eventType].label;
}

export function getXPEventIcon(eventType: XPEventType): string {
  return XP_EVENT_CONFIG[eventType].icon;
}

/**
 * Calculate XP for an event, considering special cases like streak bonus
 */
export function calculateXP(
  eventType: XPEventType,
  metadata?: { streakDays?: number; customAmount?: number }
): number {
  if (eventType === 'DAILY_STREAK' && metadata?.streakDays) {
    return Math.min(metadata.streakDays, 30); // Cap at 30 XP
  }
  if (eventType === 'MANUAL_AWARD' && metadata?.customAmount) {
    return metadata.customAmount;
  }
  return XP_EVENT_CONFIG[eventType].baseXP;
}
